import { useRef, useState, useCallback } from 'react'
import { Global } from '@emotion/react'
import DeleteIcon from '@mui/icons-material/Delete'
import { TextField, Chip, AutocompleteRenderGetTagProps } from '@mui/material'
import { useDeepCompareEffect } from 'react-use'
import { Theme } from 'Theme'
import { MenuList } from './menuList'
import * as css from './styles'
import { repositionModifier } from './utils'

let tabPressed = false

function PopperComponent(props) {
  return (
    <css.StyledAutocompletePopper
      {...props}
      className="multi-select-autocomplete-popper"
      modifiers={[repositionModifier]}
    />
  )
}

/**
 *
 * @param param0
 * @returns
 */
export const ReactMultiSelect = ({ row, data, updateData, options, selected, close, parent }) => {
  const selectRef = useRef<HTMLInputElement>(null)
  const [state, setState] = useState({
    ...data,
    selected,
    row,
    search: '',
  })

  // Set data on state
  useDeepCompareEffect(() => {
    setState(prevState => ({
      ...prevState,
      ...data,
    }))
  }, [data])

  // Set data on
  useDeepCompareEffect(() => {
    if (typeof updateData !== 'function') return
    if (state.selected === data.selected) return

    updateData(state)
  }, [updateData, state.selected])

  const handleClick = useCallback(
    (_event, newValue, reason) => {
      if (tabPressed) {
        close('TAB')
        tabPressed = false
      }

      switch (reason) {
        case 'blur':
          return
        case 'removeOption':
          updateData({
            ...state,
            selected: newValue,
          })
          return
        case 'selectOption': {
          if (Array.isArray(newValue)) {
            updateData({
              ...state,
              selected: newValue,
            })
            return
          }

          const newSelected = options
            .map((o, index) => {
              const exists = state.selected.find(s => s.label === o.label)
              const removeSelected = index === newValue && exists

              if (removeSelected) return null // comes first, to remove.
              if (exists) return o // already selected
              if (index === newValue) return o // newly selected

              return null
            })
            .filter(o => o)

          updateData({
            ...state,
            selected: newSelected,
          })
        }
      }
    },
    [options, state.selected, tabPressed, state.search],
  )

  function handleTabs(e) {
    if (e.keyCode == 9) {
      tabPressed = true
    }
    if (e.keyCode == 27) {
      // escape out of tabs
      e.stopPropagation()
      e.preventDefault()

      if (selectRef?.current) {
        selectRef?.current.blur()
      }

      close()
    }

    setState(prevState => ({ ...prevState, search: e.target.value }))
  }

  selectRef.current?.focus()
  const width = parent?.getClientRects()[0]?.width
  const height = parent?.getClientRects()[0]?.height

  return (
    <Theme
      overrides={{
        height,
        width,
      }}
    >
      <Global styles={css.Global} />
      <css.StyledAutocomplete
        multiple
        selectOnFocus
        open
        disableCloseOnSelect={true}
        ref={selectRef}
        id="multi-select"
        size="small"
        options={options}
        getOptionLabel={(option: any) => option.label}
        value={state.selected}
        inputValue={state.search}
        onKeyDown={handleTabs}
        onChange={handleClick}
        isOptionEqualToValue={(option: any, value: any) => option.value === value.value}
        onInputChange={(_event, val) => {
          setState(prevState => ({ ...prevState, search: val }))
        }}
        renderTags={(value: unknown[], getTagProps: AutocompleteRenderGetTagProps) =>
          value.map((option: unknown, index: number) => {
            if (typeof option !== 'object') return
            const { label } = option as { label: string; value: string }
            const { key, ...props } = getTagProps({ index })
            return <Chip key={key} size="small" label={label} {...props} />
          })
        }
        clearIcon={<DeleteIcon />}
        PopperComponent={PopperComponent}
        ListboxComponent={MenuList}
        ListboxProps={{
          onMouseDown: e => {
            e.preventDefault()
            e.stopPropagation()

            let elem = e.target as HTMLElement
            if (elem?.nodeName !== 'LI') {
              const parentLI = elem?.parentElement?.parentElement
              if (parentLI?.nodeName === 'LI') elem = parentLI
            }

            const idx = Number(elem.dataset.optionIndex)
            handleClick(e, idx, 'selectOption')
          },
          selected: state.selected,
          search: state.search,
          width,
          options,
        }}
        renderInput={params => (
          <TextField
            {...params}
            variant="standard"
            sx={{
              height,
              width,
            }}
          />
        )}
      />
    </Theme>
  )
}
